import React, { useMemo } from 'react'
import {
    Button,
    Flex,
    FlexProps,
    Progress,
    Text,
    Tooltip,
} from '@chakra-ui/react'
import { FaCircleInfo, FaWandMagicSparkles } from 'react-icons/fa6'

import { useClinic } from '~contexts/clinic-context'
import {
    findNonGsm7Characters,
    sanitizeForGsm7,
    shouldIgnoreSmsCharacterLimit,
    SmsSegmentInfo,
} from '~utils/sms-segments'

interface Props {
    segmentInfo: SmsSegmentInfo
    /** Per-surface maximum segments this message is allowed to use. */
    limit: number
    /**
     * Raw authored text (body / auto-reply / chat draft). Used to detect and
     * name the characters that shrink the character allowance. Pass the same
     * string that {@link onFixSpecialCharacters} should rewrite.
     */
    sourceText?: string
    /**
     * Called with the GSM-7-sanitized version of {@link sourceText}. When
     * provided, a one-click cleanup action is shown for auto-fixable
     * offenders (NBSPs, smart quotes, em dashes, etc.).
     */
    onFixSpecialCharacters?: (sanitizedText: string) => void
    /**
     * When true, render nothing until the message uses ~85% of its character
     * allowance (or is already over the limit). Useful in dense composers
     * where an always-visible count is noise for short drafts.
     */
    hideUntilNearLimit?: boolean
    containerProps?: FlexProps
}

/** Fraction of the character allowance at which the counter/meter appears. */
const SHOW_NEAR_LIMIT_AT = 0.85

/**
 * SMS length meter, framed entirely in characters — the unit users can
 * actually see and count. The per-surface segment limit is converted into a
 * character allowance for the message's current encoding, so the emoji
 * penalty shows up as "your limit dropped from 918 to 402 characters"
 * instead of an opaque jump in "segments".
 *
 * By default always shows the character count (right-aligned for placement
 * above a text input). The progress bar and plain-language guidance appear
 * once the message uses ~85% of its allowance, with a one-click cleanup for
 * invisible paste artifacts when relevant. Pass {@link hideUntilNearLimit}
 * to hide the whole counter until that same threshold.
 *
 * {@link segmentInfo} should already reflect send-path GSM-7 sanitization
 * (via {@link getClinicSmsSegmentInfo}) so the meter matches what is
 * actually sent.
 */
export default function SmsSegmentCounter({
    segmentInfo,
    limit,
    sourceText,
    onFixSpecialCharacters,
    hideUntilNearLimit = false,
    containerProps,
}: Props) {
    const { clinic } = useClinic()
    const { characterCount, encoding } = segmentInfo

    const offenders = useMemo(
        () => (sourceText ? findNonGsm7Characters(sourceText) : []),
        [sourceText],
    )

    // Clinic opt-out of soft SMS length guardrails — hide the meter entirely.
    if (shouldIgnoreSmsCharacterLimit(clinic?.tmp_feature_flags)) {
        return null
    }

    const isUcs2 = encoding === 'UCS-2'
    const maxCharacters = getCharacterAllowance(limit, isUcs2)
    const gsm7MaxCharacters = getCharacterAllowance(limit, false)

    const usage = maxCharacters > 0 ? characterCount / maxCharacters : 0
    const isNearLimit = usage >= SHOW_NEAR_LIMIT_AT
    const showMeter = isNearLimit

    const isOverLimit = characterCount > maxCharacters
    const overageCharacters = characterCount - maxCharacters
    const emojiOffenders = offenders.filter(o => !o.isAutoFixable)
    const fixableOffenders = offenders.filter(o => o.isAutoFixable)
    const canFix =
        !!onFixSpecialCharacters && !!sourceText && fixableOffenders.length > 0

    const barColorScheme = isOverLimit
        ? 'red'
        : usage >= 0.95
          ? 'orange'
          : 'green'
    const textColor = isOverLimit ? 'red.500' : 'gray.500'

    if (hideUntilNearLimit && !isNearLimit && !isOverLimit) {
        return null
    }

    return (
        <Flex
            direction="column"
            gap={1}
            align="flex-end"
            w="full"
            {...containerProps}
        >
            <Flex align="center" gap={2} justify="flex-end" maxW="full">
                {showMeter && (
                    <Progress
                        value={Math.min(100, usage * 100)}
                        colorScheme={barColorScheme}
                        size="xs"
                        borderRadius="full"
                        w="120px"
                        flexShrink={0}
                    />
                )}
                <Flex
                    align="center"
                    gap={1}
                    fontSize="xs"
                    color={textColor}
                    fontWeight={isOverLimit ? 'semibold' : 'normal'}
                    flexShrink={0}
                >
                    <Text>
                        {characterCount.toLocaleString()} /{' '}
                        {maxCharacters.toLocaleString()} characters
                    </Text>
                    <Tooltip
                        hasArrow
                        placement="top"
                        maxW="280px"
                        label="Phone carriers sometimes fail to deliver very long text messages, so there's a limit on how long a message can be. Emojis and special symbols take up extra room, which lowers the character limit."
                    >
                        <Flex as="span" align="center">
                            <FaCircleInfo />
                        </Flex>
                    </Tooltip>
                </Flex>
            </Flex>

            {isOverLimit && (
                <Text
                    fontSize="xs"
                    color="red.500"
                    fontWeight="semibold"
                    textAlign="right"
                >
                    This message is {overageCharacters.toLocaleString()}{' '}
                    {overageCharacters === 1 ? 'character' : 'characters'} over
                    the limit.{' '}
                    {emojiOffenders.length > 0
                        ? 'Shorten it, or remove the emojis to raise the limit.'
                        : 'Shorten it to send.'}
                </Text>
            )}

            {isUcs2 && emojiOffenders.length > 0 && (
                <Text
                    fontSize="xs"
                    color={isOverLimit ? 'red.600' : 'gray.600'}
                    textAlign="right"
                >
                    Emojis take up extra room, so the limit for this message is{' '}
                    {maxCharacters.toLocaleString()} characters instead of{' '}
                    {gsm7MaxCharacters.toLocaleString()}. Removing{' '}
                    {formatEmojiList(emojiOffenders)} raises it back up.
                </Text>
            )}

            {canFix && (
                <Flex
                    direction="column"
                    gap={1}
                    px={2}
                    py={1.5}
                    bg="orange.50"
                    borderWidth={1}
                    borderColor="orange.200"
                    borderRadius="md"
                    alignSelf="stretch"
                >
                    <Text fontSize="xs" color="orange.800">
                        Some hidden characters (usually pasted from Word or
                        Google Docs) are taking up extra room in this message.
                    </Text>
                    <Button
                        size="xs"
                        alignSelf="flex-start"
                        colorScheme="orange"
                        variant="outline"
                        leftIcon={<FaWandMagicSparkles />}
                        onClick={() =>
                            onFixSpecialCharacters(sanitizeForGsm7(sourceText))
                        }
                    >
                        Clean up hidden characters
                    </Button>
                </Flex>
            )}
        </Flex>
    )
}

/**
 * Converts a segment limit into the maximum character count that still fits
 * within it, for the message's current encoding. Single-segment messages get
 * the larger un-concatenated size (160/70); multi-segment messages use the
 * concatenated per-segment size (153/67).
 */
function getCharacterAllowance(limit: number, isUcs2: boolean): number {
    if (limit === 1) {
        return isUcs2 ? 70 : 160
    }
    return (isUcs2 ? 67 : 153) * limit
}

function formatEmojiList(offenders: { name: string; count: number }[]): string {
    const totalCount = offenders.reduce((sum, o) => sum + o.count, 0)
    const glyphs = offenders
        .map(o => {
            const match = o.name.match(/\((.+)\)/)
            return match ? match[1] : null
        })
        .filter(Boolean)
        .slice(0, 5)
        .join(' ')

    const label = totalCount === 1 ? 'the emoji' : `the ${totalCount} emojis`
    return glyphs ? `${label} (${glyphs})` : label
}
